import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  Menu,
  Modal,
  NavLink,
  Pagination,
  Popover,
  Progress,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure, useLocalStorage } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconArrowDown,
  IconArrowsSort,
  IconArrowUp,
  IconBookmark,
  IconBookmarkPlus,
  IconCirclePlus,
  IconColumns,
  IconDots,
  IconDownload,
  IconEdit,
  IconFilter,
  IconRobot,
  IconRobotOff,
  IconStarFilled,
  IconTrash,
  IconUpload,
  IconUserCheck,
  IconUsers,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { trpc } from '../../lib/trpc';
import { useContactsRealtime } from '../../lib/realtime';
import { ContactForm, blankContactForm, type ContactFormState } from '../../components/ContactForm';
import { FilterBuilder, type AndTree } from '../../components/FilterBuilder';
import { BulkContactActions } from '../../components/BulkContactActions';
import { PermissionGate } from '../../components/PermissionGate';
import { usePermissions } from '../../lib/permissions';
import { GroupsPanel } from '../../components/contacts/GroupsPanel';
import { LabelsPanel } from '../../components/contacts/LabelsPanel';
import { CustomFieldsPanel } from '../../components/contacts/CustomFieldsPanel';
import { useTabParam } from '../../lib/useTabParam';

/**
 * Columns the user can show/hide on the contacts table. The select checkbox,
 * Name, and row-actions columns are always visible. Visibility is persisted in
 * localStorage (see `leadsync.contacts.hiddenColumns`).
 */
const HIDEABLE_COLUMNS = [
  { key: 'channels', label: 'Channels' },
  { key: 'phone', label: 'Phone' },
  { key: 'score', label: 'Score' },
  { key: 'labels', label: 'Labels' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'bots', label: 'Bots' },
  { key: 'status', label: 'Status' },
  { key: 'lastMsg', label: 'Last msg' },
] as const;

function ContactsPage() {
  useContactsRealtime();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  // Hide write-action buttons (Import / New / Bulk delete / Export CSV) for
  // users without the corresponding permission instead of letting the click
  // surface a 403 toast. Project convention: never show "permission denied"
  // — just don't render the affordance.
  const { can } = usePermissions();
  const canManage = can('manage_contacts');
  const canExport = can('export_contacts');

  // Filter/view state is seeded from the URL search params (see the route's
  // validateSearch) and mirrored back on change, so filters survive a reload or
  // an outer-tab switch (the hub unmounts this panel) and each view/filter is a
  // shareable link.
  const sp = Route.useSearch();
  const parseFlt = (s?: string): AndTree | null => {
    if (!s) return null;
    try {
      return JSON.parse(s) as AndTree;
    } catch {
      return null;
    }
  };
  const [page, setPage] = useState(sp.page ?? 1);
  const [search, setSearch] = useState(sp.q ?? '');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [tab, setTab] = useState<'all' | 'mine' | 'unassigned'>(sp.seg ?? 'all');
  const [groupId, setGroupId] = useState<number | undefined>(sp.group);
  const [labelId, setLabelId] = useState<number | undefined>(sp.label);
  const [viewId, setViewId] = useState<number | undefined>(sp.view);
  const [sortField, setSortField] = useState<
    'createdAt' | 'firstName' | 'lastName' | 'messagedAt' | 'engagementScore' | 'waId'
  >(sp.sort ?? 'createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(sp.dir ?? 'desc');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // Inline (ad-hoc) advanced filter — same DSL as a saved view, applied without
  // persisting. `draftFilter` is the in-progress edit; `filterTree` is what the
  // query actually uses. Mutually exclusive with a selected saved view.
  const [filterTree, setFilterTree] = useState<AndTree | null>(() => parseFlt(sp.flt));
  const [draftFilter, setDraftFilter] = useState<AndTree | null>(() => parseFlt(sp.flt));
  const [filtersOpen, { open: openFilters, close: closeFilters }] = useDisclosure(false);
  const [hiddenColumns, setHiddenColumns] = useLocalStorage<string[]>({
    key: 'leadsync.contacts.hiddenColumns',
    defaultValue: [],
  });
  const showCol = (key: string) => !hiddenColumns.includes(key);
  const toggleColumn = (key: string) =>
    setHiddenColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  const list = trpc.contacts.contacts.list.useQuery({
    page,
    pageSize: 50,
    search: debouncedSearch || undefined,
    tab,
    groupId,
    labelId,
    viewId,
    sortField,
    sortDir,
    filter: filterTree ?? undefined,
  });
  const groups = trpc.contacts.groups.list.useQuery();
  const labels = trpc.contacts.labels.list.useQuery();
  const customFields = trpc.contacts.customFields.list.useQuery();
  const views = trpc.contacts.views.list.useQuery();
  const team = trpc.team.list.useQuery();

  // Option lists for the advanced FilterBuilder (Group / Label / Custom-field
  // conditions). Memoized so the builder's field list isn't rebuilt every render.
  const filterGroups = useMemo(
    () => (groups.data ?? []).map((g) => ({ id: g.id, title: g.title })),
    [groups.data],
  );
  const filterLabels = useMemo(
    () => (labels.data ?? []).map((l) => ({ id: l.id, title: l.title })),
    [labels.data],
  );
  const filterCustomFields = useMemo(
    () => (customFields.data ?? []).map((c) => ({ id: c.id, name: c.inputName })),
    [customFields.data],
  );

  // Selection is scoped to the current page/filter view: clear it whenever the
  // visible set changes so a bulk action can never touch rows the user can no
  // longer see (e.g. selecting on page 1 then paging away).
  useEffect(() => {
    setSelected(new Set());
  }, [page, tab, groupId, labelId, viewId, debouncedSearch, sortField, sortDir, filterTree]);

  // Mirror the active view + filters to the URL (replace, so it doesn't pile up
  // history). Defaults are omitted to keep links clean. State is the source of
  // truth after mount; this only writes outward, so there's no update loop.
  useEffect(() => {
    void navigate({
      to: '.',
      replace: true,
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        view: viewId,
        seg: tab === 'all' ? undefined : tab,
        group: groupId,
        label: labelId,
        q: debouncedSearch || undefined,
        sort: sortField === 'createdAt' ? undefined : sortField,
        dir: sortDir === 'desc' ? undefined : sortDir,
        page: page === 1 ? undefined : page,
        flt: filterTree ? JSON.stringify(filterTree) : undefined,
      }),
    });
  }, [
    viewId,
    tab,
    groupId,
    labelId,
    debouncedSearch,
    sortField,
    sortDir,
    page,
    filterTree,
    navigate,
  ]);

  // Mirror to chat.contacts on every mutation so the chat sidebar stays in
  // sync when the agent edits from the standalone contacts page.
  const refreshContactSurfaces = () => {
    void utils.contacts.contacts.list.invalidate();
    void utils.chat.contacts.invalidate();
  };

  const toggleAi = trpc.contacts.contacts.toggleAiBot.useMutation({
    onSuccess: refreshContactSurfaces,
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const toggleReply = trpc.contacts.contacts.toggleReplyBot.useMutation({
    onSuccess: refreshContactSurfaces,
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const assign = trpc.contacts.contacts.assignToUser.useMutation({
    onSuccess: () => {
      refreshContactSurfaces();
      notifications.show({ message: 'Assignment updated', color: 'green' });
    },
  });
  const remove = trpc.contacts.contacts.delete.useMutation({
    onSuccess: () => {
      refreshContactSurfaces();
      notifications.show({ message: 'Contact deleted', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const create = trpc.contacts.contacts.create.useMutation({
    onSuccess: () => {
      refreshContactSurfaces();
      notifications.show({ message: 'Contact created', color: 'green' });
      newClose();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const [newOpen, { open: openNew, close: newClose }] = useDisclosure(false);
  const [newForm, setNewForm] = useState<ContactFormState>(blankContactForm());

  const [viewModalOpen, { open: openViewModal, close: closeViewModal }] = useDisclosure(false);
  const [viewName, setViewName] = useState('');
  const [viewShared, setViewShared] = useState(true);
  const [viewTree, setViewTree] = useState<AndTree | null>(null);
  const createView = trpc.contacts.views.create.useMutation({
    onSuccess: () => {
      void utils.contacts.views.list.invalidate();
      closeViewModal();
      setViewName('');
      setViewTree(null);
      notifications.show({ message: 'View saved', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const totalPages = list.data ? Math.ceil(list.data.totalCount / list.data.pageSize) : 1;
  const ids = useMemo(() => (list.data?.items ?? []).map((c) => c.id), [list.data]);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSelected = ids.some((id) => selected.has(id));
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(ids));
  };
  const toggleOne = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const applyFilter = () => {
    setFilterTree(draftFilter);
    if (draftFilter) setViewId(undefined); // a saved view + ad-hoc filter are mutually exclusive
    setPage(1);
    closeFilters();
  };
  const clearFilter = () => {
    setDraftFilter(null);
    setFilterTree(null);
    setPage(1);
    closeFilters();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Group align="flex-start" gap="md" wrap="nowrap">
        {/* Saved views sidebar */}
        <Card w={220} p="xs" style={{ flexShrink: 0 }}>
          <Stack gap={4}>
            <Group justify="space-between">
              <Text fw={600} size="sm">
                Views
              </Text>
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => {
                  // Seed the modal with the active advanced filter so "Save filter
                  // as view" actually captures what's currently applied.
                  setViewTree(filterTree);
                  setViewName('');
                  openViewModal();
                }}
              >
                <IconBookmarkPlus size={14} />
              </ActionIcon>
            </Group>
            <NavLink
              label="All contacts"
              leftSection={<IconUsers size={14} />}
              active={!viewId}
              onClick={() => {
                // True "show everything" — clear the saved view AND every active
                // filter (advanced + quick filters + search), not just the view.
                setViewId(undefined);
                setFilterTree(null);
                setDraftFilter(null);
                setSearch('');
                setTab('all');
                setGroupId(undefined);
                setLabelId(undefined);
                setPage(1);
              }}
            />
            {(views.data ?? []).map((v) => (
              <NavLink
                key={v.id}
                label={v.name}
                leftSection={<IconBookmark size={14} />}
                rightSection={
                  v.isShared ? (
                    <Badge size="xs" variant="light">
                      shared
                    </Badge>
                  ) : null
                }
                active={viewId === v.id}
                onClick={() => {
                  setViewId(v.id);
                  setFilterTree(null);
                  // Show the view's saved filters in the builder so the view
                  // visibly "owns" them (server-side filtering still uses viewId).
                  setDraftFilter(parseFlt(v.definitionJson));
                  setPage(1);
                }}
              />
            ))}
          </Stack>
        </Card>

        <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between">
            <Title order={2}>Contacts</Title>
            <Group>
              {canExport && (
                <Button
                  component="a"
                  href={`/api/contacts/export.csv${viewId ? `?viewId=${viewId}` : ''}`}
                  variant="default"
                  leftSection={<IconDownload size={14} />}
                >
                  Export CSV
                </Button>
              )}
              {canManage && (
                <Button
                  component={Link}
                  to="/vendor/contacts/import"
                  variant="default"
                  leftSection={<IconUpload size={14} />}
                >
                  Import
                </Button>
              )}
              {canManage && (
                <Button
                  onClick={() => {
                    setNewForm(blankContactForm());
                    openNew();
                  }}
                  leftSection={<IconCirclePlus size={14} />}
                >
                  New contact
                </Button>
              )}
            </Group>
          </Group>

          <Card p="sm">
            <Group align="center" gap="xs" wrap="nowrap">
              <TextInput
                flex={1}
                miw={200}
                size="sm"
                placeholder="Search name, phone, email, custom field…"
                value={search}
                onChange={(e) => {
                  setSearch(e.currentTarget.value);
                  setPage(1);
                }}
              />
              <Select
                size="sm"
                w={130}
                data={[
                  { value: 'all', label: 'All' },
                  { value: 'mine', label: 'Mine' },
                  { value: 'unassigned', label: 'Unassigned' },
                ]}
                value={tab}
                onChange={(v) => {
                  setTab((v as typeof tab) ?? 'all');
                  setPage(1);
                }}
              />
              <Select
                size="sm"
                w={150}
                placeholder="Group"
                clearable
                data={(groups.data ?? []).map((g) => ({
                  value: String(g.id),
                  label: g.title,
                }))}
                value={groupId ? String(groupId) : null}
                onChange={(v) => {
                  setGroupId(v ? Number(v) : undefined);
                  setPage(1);
                }}
              />
              <Select
                size="sm"
                w={150}
                placeholder="Label"
                clearable
                data={(labels.data ?? []).map((l) => ({ value: String(l.id), label: l.title }))}
                value={labelId ? String(labelId) : null}
                onChange={(v) => {
                  setLabelId(v ? Number(v) : undefined);
                  setPage(1);
                }}
              />
              <Popover
                opened={filtersOpen}
                onChange={(o) => {
                  if (o) {
                    setDraftFilter(filterTree);
                    openFilters();
                  } else {
                    closeFilters();
                  }
                }}
                position="bottom-start"
                width={480}
                withinPortal
                trapFocus
              >
                <Popover.Target>
                  <Button
                    size="sm"
                    variant={filterTree ? 'light' : 'default'}
                    leftSection={<IconFilter size={14} />}
                  >
                    Filters{filterTree?.terms.length ? ` (${filterTree.terms.length})` : ''}
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <Stack gap="sm">
                    <Text size="sm" fw={500}>
                      Advanced filter
                    </Text>
                    <FilterBuilder
                      value={draftFilter}
                      onChange={setDraftFilter}
                      groups={filterGroups}
                      labels={filterLabels}
                      customFields={filterCustomFields}
                    />
                    <Group justify="space-between">
                      <Button variant="subtle" color="gray" size="xs" onClick={clearFilter}>
                        Clear
                      </Button>
                      <Button size="xs" onClick={applyFilter}>
                        Apply
                      </Button>
                    </Group>
                  </Stack>
                </Popover.Dropdown>
              </Popover>
              <Popover position="bottom-end" withinPortal trapFocus>
                <Popover.Target>
                  <Button size="sm" variant="default" leftSection={<IconColumns size={14} />}>
                    Columns
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <Stack gap="xs">
                    <Text size="sm" fw={500}>
                      Show columns
                    </Text>
                    {HIDEABLE_COLUMNS.map((col) => (
                      <Checkbox
                        key={col.key}
                        label={col.label}
                        checked={showCol(col.key)}
                        onChange={() => toggleColumn(col.key)}
                      />
                    ))}
                  </Stack>
                </Popover.Dropdown>
              </Popover>
              {someSelected && canManage && (
                <BulkContactActions
                  selectedIds={Array.from(selected)}
                  labels={labels.data ?? []}
                  groups={groups.data ?? []}
                  onDone={() => {
                    refreshContactSurfaces();
                    setSelected(new Set());
                  }}
                />
              )}
            </Group>
          </Card>

          <Card p={0}>
            {list.isPending ? (
              <Skeleton height={400} m="md" />
            ) : list.isError ? (
              <Text c="red" p="md">
                {list.error.message}
              </Text>
            ) : list.data.items.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                No contacts match your filters yet.
              </Text>
            ) : (
              <Table striped highlightOnHover withRowBorders={false}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={32}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={toggleAll}
                      />
                    </Table.Th>
                    <SortableTh
                      field="firstName"
                      currentField={sortField}
                      currentDir={sortDir}
                      onSort={(f, d) => {
                        setSortField(f as typeof sortField);
                        setSortDir(d);
                        setPage(1);
                      }}
                    >
                      Name
                    </SortableTh>
                    {showCol('channels') && <Table.Th>Channels</Table.Th>}
                    {showCol('phone') && (
                      <SortableTh
                        field="waId"
                        currentField={sortField}
                        currentDir={sortDir}
                        onSort={(f, d) => {
                          setSortField(f as typeof sortField);
                          setSortDir(d);
                          setPage(1);
                        }}
                      >
                        Phone
                      </SortableTh>
                    )}
                    {showCol('score') && (
                      <SortableTh
                        field="engagementScore"
                        currentField={sortField}
                        currentDir={sortDir}
                        onSort={(f, d) => {
                          setSortField(f as typeof sortField);
                          setSortDir(d);
                          setPage(1);
                        }}
                      >
                        Score
                      </SortableTh>
                    )}
                    {showCol('labels') && <Table.Th>Labels</Table.Th>}
                    {showCol('assigned') && <Table.Th>Assigned</Table.Th>}
                    {showCol('bots') && <Table.Th>Bots</Table.Th>}
                    {showCol('status') && <Table.Th>Status</Table.Th>}
                    {showCol('lastMsg') && (
                      <SortableTh
                        field="messagedAt"
                        currentField={sortField}
                        currentDir={sortDir}
                        onSort={(f, d) => {
                          setSortField(f as typeof sortField);
                          setSortDir(d);
                          setPage(1);
                        }}
                      >
                        Last msg
                      </SortableTh>
                    )}
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {list.data.items.map((c) => (
                    <Table.Tr key={c.id}>
                      <Table.Td>
                        <Checkbox checked={selected.has(c.id)} onChange={() => toggleOne(c.id)} />
                      </Table.Td>
                      <Table.Td>
                        <Text
                          fw={500}
                          size="sm"
                          style={{ cursor: 'pointer', color: 'var(--mantine-color-blue-6)' }}
                          onClick={() =>
                            navigate({ to: '/vendor/contacts/$uid', params: { uid: c.uid } })
                          }
                        >
                          {c.fullName || '—'}
                        </Text>
                      </Table.Td>
                      {showCol('channels') && (
                        <Table.Td>
                          <Group gap={4}>
                            {c.channels.includes('whatsapp') && (
                              <Tooltip label="WhatsApp">
                                <Badge size="xs" color="green" variant="light">
                                  WA
                                </Badge>
                              </Tooltip>
                            )}
                            {c.channels.includes('messenger') && (
                              <Tooltip label="Messenger">
                                <Badge size="xs" color="blue" variant="light">
                                  FB
                                </Badge>
                              </Tooltip>
                            )}
                            {c.channels.includes('instagram') && (
                              <Tooltip label="Instagram">
                                <Badge size="xs" color="pink" variant="light">
                                  IG
                                </Badge>
                              </Tooltip>
                            )}
                          </Group>
                        </Table.Td>
                      )}
                      {showCol('phone') && (
                        <Table.Td>
                          <Text size="sm" ff="monospace">
                            {c.waIdDisplay ?? '—'}
                          </Text>
                        </Table.Td>
                      )}
                      {showCol('score') && (
                        <Table.Td>
                          <ScoreBadge score={c.engagementScore} />
                        </Table.Td>
                      )}
                      {showCol('labels') && (
                        <Table.Td>
                          <Group gap={4} wrap="wrap">
                            {c.labels.map((l) => (
                              <Badge
                                key={l.id}
                                size="xs"
                                variant="light"
                                style={{
                                  backgroundColor: l.bgColor ?? undefined,
                                  color: l.textColor ?? undefined,
                                }}
                              >
                                {l.title}
                              </Badge>
                            ))}
                          </Group>
                        </Table.Td>
                      )}
                      {showCol('assigned') && (
                        <Table.Td>
                          <Select
                            size="xs"
                            w={140}
                            placeholder="Unassigned"
                            searchable
                            clearable
                            data={(team.data ?? []).map((t) => ({
                              value: t.user?.id ?? String(t.id),
                              label: t.user?.displayName ?? t.user?.email ?? 'Unknown',
                            }))}
                            value={c.assignedUserId ?? null}
                            onChange={(v) => assign.mutate({ contactId: c.id, userId: v ?? null })}
                          />
                        </Table.Td>
                      )}
                      {showCol('bots') && (
                        <Table.Td>
                          <Group gap={4}>
                            <ActionIcon
                              variant="subtle"
                              color={c.isAiBotDisabled ? 'gray' : 'blue'}
                              aria-label="Toggle AI bot"
                              onClick={() => toggleAi.mutate({ contactId: c.id })}
                            >
                              {c.isAiBotDisabled ? (
                                <IconRobotOff size={14} />
                              ) : (
                                <IconRobot size={14} />
                              )}
                            </ActionIcon>
                            <Switch
                              size="xs"
                              checked={!c.isReplyBotDisabled}
                              onChange={() => toggleReply.mutate({ contactId: c.id })}
                              aria-label="Toggle reply bot"
                            />
                          </Group>
                        </Table.Td>
                      )}
                      {showCol('status') && (
                        <Table.Td>
                          <Group gap={4}>
                            {c.isOptedOut && (
                              <Badge color="orange" variant="light" size="xs">
                                opted out
                              </Badge>
                            )}
                            {c.isUnreachable && (
                              <Tooltip
                                label={`Meta error ${c.lastSendErrorCode} — recipient unreachable`}
                              >
                                <Badge color="red" variant="light" size="xs">
                                  unreachable
                                </Badge>
                              </Tooltip>
                            )}
                            {c.isBlocked && (
                              <Badge color="red" variant="light" size="xs">
                                blocked
                              </Badge>
                            )}
                            {c.unreadMessagesCount > 0 && (
                              <Badge color="blue" variant="light" size="xs">
                                {c.unreadMessagesCount} unread
                              </Badge>
                            )}
                          </Group>
                        </Table.Td>
                      )}
                      {showCol('lastMsg') && (
                        <Table.Td>
                          <Text size="xs" c="dimmed">
                            {c.messagedAt ? new Date(c.messagedAt).toLocaleDateString() : '—'}
                          </Text>
                        </Table.Td>
                      )}
                      <Table.Td align="right">
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon variant="subtle">
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconEdit size={14} />}
                              onClick={() =>
                                navigate({
                                  to: '/vendor/contacts/$uid',
                                  params: { uid: c.uid },
                                })
                              }
                            >
                              Open detail
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconUserCheck size={14} />}
                              onClick={() => assign.mutate({ contactId: c.id, userId: null })}
                            >
                              Unassign
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              onClick={() => remove.mutate({ contactId: c.id })}
                            >
                              Delete
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Card>

          <Group justify="space-between">
            <Text c="dimmed" size="sm">
              {list.data ? `${list.data.totalCount} contacts` : ''}
              {viewId && views.data
                ? ` (filtered by ${views.data.find((v) => v.id === viewId)?.name ?? 'view'})`
                : ''}
            </Text>
            <Pagination value={page} onChange={setPage} total={totalPages} />
          </Group>
        </Stack>
      </Group>

      {/* New contact modal */}
      <Modal opened={newOpen} onClose={newClose} title="New contact" size="lg" centered>
        <Stack>
          <ContactForm state={newForm} onChange={setNewForm} />
          <Group justify="end">
            <Button variant="default" onClick={newClose}>
              Cancel
            </Button>
            <Button
              loading={create.isPending}
              onClick={() =>
                create.mutate({
                  firstName: newForm.firstName || null,
                  lastName: newForm.lastName || null,
                  waId: newForm.waId || null,
                  email: newForm.email || null,
                  languageCode: newForm.languageCode || null,
                  countryId: newForm.countryId,
                  whatsappOptOut: newForm.whatsappOptOut,
                  enableAiBot: newForm.enableAiBot,
                  enableReplyBot: newForm.enableReplyBot,
                  groupIds: newForm.groupIds,
                  customFieldValues: newForm.customFieldValues,
                })
              }
            >
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Save view modal */}
      <Modal
        opened={viewModalOpen}
        onClose={closeViewModal}
        title="Save filter as view"
        size="lg"
        centered
      >
        <Stack>
          <TextInput
            label="View name"
            required
            value={viewName}
            onChange={(e) => setViewName(e.currentTarget.value)}
          />
          <Switch
            label="Share with team"
            checked={viewShared}
            onChange={(e) => setViewShared(e.currentTarget.checked)}
          />
          <Box>
            <Text size="sm" fw={500} mb="xs">
              Conditions (all must match)
            </Text>
            <FilterBuilder
              value={viewTree}
              onChange={setViewTree}
              groups={filterGroups}
              labels={filterLabels}
              customFields={filterCustomFields}
            />
          </Box>
          <Group justify="end">
            <Button variant="default" onClick={closeViewModal}>
              Cancel
            </Button>
            <Button
              loading={createView.isPending}
              disabled={!viewName.trim() || !viewTree || viewTree.terms.length === 0}
              onClick={() => {
                if (!viewTree) return;
                createView.mutate({
                  name: viewName,
                  isShared: viewShared,
                  definition: viewTree,
                });
              }}
            >
              Save view
            </Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}

function SortableTh(props: {
  field: string;
  currentField: string;
  currentDir: 'asc' | 'desc';
  onSort: (field: string, dir: 'asc' | 'desc') => void;
  children: React.ReactNode;
}) {
  const isActive = props.currentField === props.field;
  const Icon = !isActive
    ? IconArrowsSort
    : props.currentDir === 'asc'
      ? IconArrowUp
      : IconArrowDown;
  return (
    <Table.Th
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() =>
        props.onSort(props.field, isActive && props.currentDir === 'desc' ? 'asc' : 'desc')
      }
    >
      <Group gap={4} wrap="nowrap">
        <span>{props.children}</span>
        <Icon size={12} opacity={isActive ? 1 : 0.4} />
      </Group>
    </Table.Th>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 70 ? 'green' : score >= 40 ? 'yellow' : 'gray';
  return (
    <Tooltip label={`Engagement score ${score}/100`}>
      <Group gap={4} wrap="nowrap">
        <Progress value={score} w={60} size="sm" color={color} />
        {score >= 70 && <IconStarFilled size={10} color={`var(--mantine-color-${color}-6)`} />}
      </Group>
    </Tooltip>
  );
}

const CONTACT_TABS = ['contacts', 'groups', 'labels', 'custom-fields'] as const;
type ContactTab = (typeof CONTACT_TABS)[number];

/** Contacts hub — the single "Contacts" sidebar entry. Folds the former
 *  separate Groups / Labels / Custom-fields pages into tabs; the active tab is
 *  URL-driven (`?tab=`) so the nav, old-route redirects, and refresh all land
 *  in the right place. Auto-labels + CRM connections keep their own routes
 *  (reached from the Labels tab / the Connections menu respectively). */
function ContactsHub() {
  const [tab, setTab] = useTabParam<ContactTab>('tab', CONTACT_TABS, 'contacts');
  return (
    <PermissionGate permission="manage_contacts">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <Tabs
          value={tab}
          onChange={(v) => setTab((v as ContactTab) ?? 'contacts')}
          keepMounted={false}
        >
          <Tabs.List mb="md">
            <Tabs.Tab value="contacts">Contacts</Tabs.Tab>
            <Tabs.Tab value="groups">Groups</Tabs.Tab>
            <Tabs.Tab value="labels">Labels</Tabs.Tab>
            <Tabs.Tab value="custom-fields">Custom fields</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="contacts">
            <ContactsPage />
          </Tabs.Panel>
          <Tabs.Panel value="groups">
            <GroupsPanel />
          </Tabs.Panel>
          <Tabs.Panel value="labels">
            <LabelsPanel />
          </Tabs.Panel>
          <Tabs.Panel value="custom-fields">
            <CustomFieldsPanel />
          </Tabs.Panel>
        </Tabs>
      </motion.div>
    </PermissionGate>
  );
}

interface ContactsHubSearch {
  tab?: ContactTab;
  view?: number;
  seg?: 'all' | 'mine' | 'unassigned';
  group?: number;
  label?: number;
  q?: string;
  sort?: 'createdAt' | 'firstName' | 'lastName' | 'messagedAt' | 'engagementScore' | 'waId';
  dir?: 'asc' | 'desc';
  page?: number;
  flt?: string;
}

export const Route = createFileRoute('/vendor/contacts/')({
  component: ContactsHub,
  // `tab` selects the hub panel; the rest persist the Contacts list's view +
  // filters so they survive a reload / tab switch and are shareable links.
  validateSearch: (s: Record<string, unknown>): ContactsHubSearch => {
    const num = (v: unknown): number | undefined => {
      const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
      return Number.isFinite(n) ? n : undefined;
    };
    const oneOf = <T extends string>(v: unknown, allowed: readonly T[]): T | undefined =>
      typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;
    return {
      tab: oneOf(s.tab, CONTACT_TABS),
      seg: oneOf(s.seg, ['all', 'mine', 'unassigned'] as const),
      sort: oneOf(s.sort, [
        'createdAt',
        'firstName',
        'lastName',
        'messagedAt',
        'engagementScore',
        'waId',
      ] as const),
      dir: oneOf(s.dir, ['asc', 'desc'] as const),
      view: num(s.view),
      group: num(s.group),
      label: num(s.label),
      page: num(s.page),
      q: typeof s.q === 'string' && s.q ? s.q : undefined,
      flt: typeof s.flt === 'string' && s.flt ? s.flt : undefined,
    };
  },
});
